/**
 * Formatação de dados fitness: Google Health API v4 e legado Google Health (aggregate).
 */

import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { IGoogleHealthFitnessBundle } from "@/services/google/health";
import type {
  IGoogleHealthDataPoint,
  IGoogleHealthTimeInterval,
} from "@/services/google/health/types";
import {
  getIntervalCivilStart,
  heartRateSampleOnSelectedDay,
  isSameCivilDay,
  sampleOnSelectedDay,
  sleepEndsOnSelectedDay,
} from "@/services/google/health";

export interface IFormattedFitData {
  passos: number;
  calorias: number;
  distanciaMetros: number;
  bpmMedio: number;
  minutosAtivos: number;
  glicose: number;
  sonoMinutos: number;
  pressaoSistolica: number;
  pressaoDiastolica: number;
  pressao: string;
  pesoKg: number;
}

/**
 * Converte total em minutos (API / FHIR) para texto legível: "7h 35min", "45 min", "8h".
 */
export function formatSleepDurationFromMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0 min";
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m} min`;
}

/**
 * Distância legível: metros abaixo de 1 km; quilômetros com 1 decimal acima disso.
 */
export function formatDistanceFromMeters(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return "0 m";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/** Peso legível em quilogramas. */
export function formatWeightFromKg(kg: number): string {
  if (!Number.isFinite(kg) || kg <= 0) return "0 kg";
  return `${kg.toFixed(1)} kg`;
}

function getPointValue(point: any, valueIndex = 0) {
  const value = point?.value?.[valueIndex];
  if (!value) return 0;
  if (typeof value.intVal === "number") return value.intVal;
  if (typeof value.fpVal === "number") return value.fpVal;
  return 0;
}

const NANOS_LOOKS_LIKE_MS_CUTOFF = 1_000_000_000_000_000n;

function toEpochNanos(raw: string | number | bigint): bigint {
  const n = BigInt(String(raw));
  if (n > 0n && n < NANOS_LOOKS_LIKE_MS_CUTOFF) return n * 1_000_000n;
  return n;
}

function mergeDatasetsAcrossBuckets(data: any): any[] {
  const buckets = data?.bucket || [];
  if (!buckets.length) return [];
  const maxLen = Math.max(
    0,
    ...buckets.map((b: any) => b?.dataset?.length ?? 0)
  );
  const merged: any[] = [];
  for (let i = 0; i < maxLen; i++) {
    const points: any[] = [];
    let dataSourceId = "";
    for (const b of buckets) {
      const ds = b?.dataset?.[i];
      if (!ds) continue;
      if (ds.dataSourceId) dataSourceId = ds.dataSourceId;
      if (Array.isArray(ds.point) && ds.point.length) points.push(...ds.point);
    }
    merged.push({ dataSourceId, point: points });
  }
  return merged;
}

const LEGACY_SLEEP_INDEX = 7;

function resolveSleepDataset(datasets: any[]): any {
  const atIndex = datasets[LEGACY_SLEEP_INDEX];
  if (atIndex?.point?.length) return atIndex;
  return (
    datasets.find(
      (ds: any) =>
        String(ds?.dataSourceId || "").includes("sleep.segment") &&
        ds?.point?.length
    ) || atIndex
  );
}

function computeSleepMinutesFromPoints(points: any[]): number {
  let totalNanos = 0n;
  for (const p of points) {
    const start = p?.startTimeNanos;
    const end = p?.endTimeNanos;
    if (start != null && end != null) {
      try {
        const s = toEpochNanos(start);
        const e = toEpochNanos(end);
        if (e > s) totalNanos += e - s;
      } catch {
        /* ignore */
      }
    }
  }
  if (totalNanos > 0n) {
    const minutes = Number(totalNanos / 1_000_000_000n) / 60;
    return Math.round(minutes * 10) / 10;
  }
  const fallback = points[0] ? getPointValue(points[0], 0) : 0;
  if (fallback > 0 && fallback <= 24 * 60) return fallback;
  return 0;
}

function computeSleepMinutesTotal(pointSleep: any, sleepDataset: any): number {
  const points = sleepDataset?.point || [];
  if (points.length) return computeSleepMinutesFromPoints(points);
  const fallback = getPointValue(pointSleep, 0);
  if (fallback > 0 && fallback <= 24 * 60) return fallback;
  return 0;
}

/**
 * Minutos de sono para o dia escolhido no calendário, alinhado ao resumo do Google Health:
 * soma a duração **completa** de cada segmento cujo **fim** cai nesse dia civil (dia do despertar).
 * Antes usávamos só a parte do segmento dentro de 00:00–23:59, o que cortava a noite na meia-noite
 * e gerava totais menores ou inconsistentes com o app.
 */
export function extractSleepMinutesForSelectedDay(
  sleepAggregateOrSynthetic: any,
  selectedDate: Dayjs
): number {
  const datasets = mergeDatasetsAcrossBuckets(sleepAggregateOrSynthetic);
  const sleepDs =
    datasets.find((ds: any) =>
      String(ds?.dataSourceId || "").includes("sleep.segment")
    ) ||
    datasets.find(
      (ds: any) =>
        String(ds?.dataSourceId || "").includes("sleep") && ds?.point?.length
    ) ||
    datasets[0];

  const points = sleepDs?.point || [];

  let totalMs = 0;
  for (const p of points) {
    if (p?.startTimeNanos == null || p?.endTimeNanos == null) continue;
    try {
      const startMs = Number(toEpochNanos(p.startTimeNanos) / 1_000_000n);
      const endMs = Number(toEpochNanos(p.endTimeNanos) / 1_000_000n);
      if (endMs <= startMs) continue;
      if (dayjs(endMs).isSame(selectedDate, "day")) {
        totalMs += endMs - startMs;
      }
    } catch {
      /* ignore */
    }
  }
  if (totalMs > 0) {
    return Math.round((totalMs / 60000) * 10) / 10;
  }
  return 0;
}

/**
 * Pontos crus (GET dataset) — mesmo critério “dia do despertar” que extractSleepMinutesForSelectedDay.
 */
export function extractSleepMinutesFromRawPoints(
  points: any[],
  selectedDate: Dayjs
): number {
  if (!points?.length) return 0;
  const synthetic = {
    bucket: [
      {
        dataset: [{ dataSourceId: "raw:sleep.segment", point: points }],
      },
    ],
  };
  return extractSleepMinutesForSelectedDay(synthetic, selectedDate);
}

/**
 * @param sleepAggregate Resposta aggregate só de sono; ou sintético com pontos crus.
 */
export function formatarDadosFit(
  mainAggregate: any,
  sleepAggregate?: any | null,
  selectedDate?: Dayjs,
  /** Fallback quando aggregate não traz pontos (GET em dataSources/datasets). */
  sleepRawPoints?: any[] | null
): IFormattedFitData {
  const datasets = mergeDatasetsAcrossBuckets(mainAggregate);
  const pointSteps = datasets[0]?.point?.[0];
  const pointCalories = datasets[1]?.point?.[0];
  const pointDistance = datasets[2]?.point?.[0];
  const pointHeart = datasets[3]?.point?.[0];
  const pointActiveMinutes = datasets[4]?.point?.[0];
  const pointGlucose = datasets[5]?.point?.[0];
  const pointPressure = datasets[6]?.point?.[0];

  const systolicRaw = getPointValue(pointPressure, 0);
  const diastolicRaw =
    getPointValue(pointPressure, 3) ||
    getPointValue(pointPressure, 1) ||
    getPointValue(pointPressure, 2);
  const systolic = Math.round(systolicRaw);
  const diastolic = Math.round(diastolicRaw);

  let sonoMinutos = 0;
  if (selectedDate != null) {
    if (sleepAggregate != null) {
      sonoMinutos = extractSleepMinutesForSelectedDay(sleepAggregate, selectedDate);
    }
    if (
      sonoMinutos <= 0 &&
      sleepRawPoints != null &&
      sleepRawPoints.length > 0
    ) {
      sonoMinutos = extractSleepMinutesFromRawPoints(sleepRawPoints, selectedDate);
    }
  } else if (sleepAggregate === undefined && selectedDate === undefined) {
    const sleepDataset = resolveSleepDataset(datasets);
    const pointSleep = sleepDataset?.point?.[0];
    sonoMinutos = computeSleepMinutesTotal(pointSleep, sleepDataset);
  }

  return {
    passos: getPointValue(pointSteps, 0),
    calorias: Math.round(getPointValue(pointCalories, 0)),
    distanciaMetros: Math.round(getPointValue(pointDistance, 0)),
    bpmMedio: Math.round(getPointValue(pointHeart, 0)),
    minutosAtivos: Math.round(getPointValue(pointActiveMinutes, 0)),
    glicose: Math.round(getPointValue(pointGlucose, 0)),
    sonoMinutos,
    pressaoSistolica: systolic,
    pressaoDiastolica: diastolic,
    pressao: `${systolic}/${diastolic} mmHg`,
    pesoKg: 0,
  };
}

function parseApiInt64(value: string | number | undefined): number {
  if (value == null || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function pointBelongsToSelectedDay(
  interval: IGoogleHealthTimeInterval | undefined,
  selectedDate: Dayjs
): boolean {
  const civilStart = getIntervalCivilStart(interval);
  if (civilStart) return isSameCivilDay(civilStart, selectedDate);
  if (!interval?.startTime) return false;
  return dayjs(interval.startTime).isSame(selectedDate, "day");
}

function sumStepsForDay(
  points: IGoogleHealthFitnessBundle["steps"],
  selectedDate: Dayjs,
  allowWholeBundleFallback: boolean
): number {
  if (!points.length) return 0;

  let total = 0;
  for (const point of points) {
    const interval = point.steps?.interval;
    if (pointBelongsToSelectedDay(interval, selectedDate)) {
      total += parseApiInt64(point.steps?.count);
    }
  }
  if (total > 0) return total;

  // Fallback legado (soma tudo): só permitido quando o fetch foi de 1 dia.
  // Em bundle de período isso vazava passos de outros dias para o dia vazio.
  if (!allowWholeBundleFallback) return 0;

  return points.reduce(
    (sum, point) => sum + parseApiInt64(point.steps?.count),
    0
  );
}

function sumDistanceMetersForDay(
  points: IGoogleHealthFitnessBundle["distance"],
  selectedDate: Dayjs,
  allowWholeBundleFallback: boolean
): number {
  if (!points.length) return 0;

  let totalMm = 0;
  for (const point of points) {
    const interval = point.distance?.interval;
    if (pointBelongsToSelectedDay(interval, selectedDate)) {
      totalMm += parseApiInt64(point.distance?.millimeters);
    }
  }
  if (totalMm <= 0) {
    // Fallback legado (soma tudo): só permitido quando o fetch foi de 1 dia.
    // Em bundle de período isso atribuía distância de outros dias ao dia vazio.
    if (!allowWholeBundleFallback) return 0;

    totalMm = points.reduce(
      (sum, point) => sum + parseApiInt64(point.distance?.millimeters),
      0
    );
  }

  return Math.round((totalMm / 1000) * 10) / 10;
}


/**
 * Soma o sono de uma noite (vários pontos com despertar no mesmo dia).
 * Se houver `summary.minutesAsleep`, usa o maior valor (evita somar sessões duplicadas).
 * Sem summary, soma a duração dos intervalos (segmentos/estágios).
 */
function computeSleepMinutesForWakeDayPoints(
  dayPoints: IGoogleHealthDataPoint[]
): number {
  const summaries = dayPoints
    .map((point) => parseApiInt64(point.sleep?.summary?.minutesAsleep))
    .filter((minutes) => minutes > 0);

  if (summaries.length > 0) {
    return Math.max(...summaries);
  }

  let totalMinutes = 0;
  for (const point of dayPoints) {
    const interval = point.sleep?.interval;
    if (!interval?.startTime || !interval?.endTime) continue;
    const startMs = dayjs(interval.startTime).valueOf();
    const endMs = dayjs(interval.endTime).valueOf();
    if (endMs > startMs) {
      totalMinutes += Math.round((endMs - startMs) / 60000);
    }
  }
  return totalMinutes;
}

/**
 * Sono atribuído ao dia do despertar (fim da sessão no dia selecionado).
 * Com fetch de período, o fallback que soma todo o bundle inflava o valor — evitamos isso.
 */
function extractSleepMinutesFromGoogleHealth(
  points: IGoogleHealthFitnessBundle["sleep"],
  selectedDate: Dayjs,
  allowWholeBundleFallback: boolean
): number {
  if (!points.length) return 0;

  const dayPoints: IGoogleHealthDataPoint[] = [];
  const seenSessionKeys = new Set<string>();

  for (const point of points) {
    const interval = point.sleep?.interval;
    if (!sleepEndsOnSelectedDay(interval, selectedDate)) continue;

    // Evita contar a mesma sessão duas vezes se a API repetir o ponto.
    const sessionKey =
      interval?.endTime ?? point.sleep?.updateTime ?? point.sleep?.createTime ?? "";
    if (sessionKey && seenSessionKeys.has(sessionKey)) continue;
    if (sessionKey) seenSessionKeys.add(sessionKey);

    dayPoints.push(point);
  }

  if (dayPoints.length > 0) {
    return Math.round(computeSleepMinutesForWakeDayPoints(dayPoints) * 10) / 10;
  }

  // Fallback legado (soma tudo): só permitido quando o fetch foi de 1 dia.
  // Em bundle de período isso atribuía sono de outras noites ao dia vazio.
  if (!allowWholeBundleFallback) return 0;

  const fallbackTotal = computeSleepMinutesForWakeDayPoints(points);
  return Math.round(fallbackTotal * 10) / 10;
}

/**
 * Média de BPM do ECG. O filtro da API de ECG é aberto (só `start_time >=`),
 * então SEMPRE filtramos pelo dia selecionado aqui; sem isso, ECG de outros
 * dias entrava na média (e gerava observation para dia sem registro).
 */
function averageEcgBpm(
  points: IGoogleHealthFitnessBundle["electrocardiogram"],
  selectedDate: Dayjs,
  allowWholeBundleFallback: boolean
): number {
  const onDay = points.filter((point) =>
    pointBelongsToSelectedDay(point.electrocardiogram?.interval, selectedDate)
  );
  // Fallback legado (usa todos os pontos): só quando o fetch foi de 1 dia.
  const source =
    onDay.length > 0 ? onDay : allowWholeBundleFallback ? points : [];

  const values = source
    .map((p) => parseApiInt64(p.electrocardiogram?.beatsPerMinuteAvg))
    .filter((v) => v > 0);
  if (!values.length) return 0;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function collectHeartRateBpms(
  points: IGoogleHealthFitnessBundle["heartRate"],
  selectedDate: Dayjs,
  filterByDay: boolean
): number[] {
  const values: number[] = [];
  for (const point of points) {
    if (filterByDay && !heartRateSampleOnSelectedDay(point.heartRate, selectedDate)) {
      continue;
    }
    const bpm = parseApiInt64(point.heartRate?.beatsPerMinute);
    if (bpm > 0) values.push(bpm);
  }
  return values;
}

function resolveAverageHeartRateBpm(
  bundle: IGoogleHealthFitnessBundle,
  selectedDate: Dayjs,
  allowWholeBundleFallback: boolean
): number {
  let values = collectHeartRateBpms(bundle.heartRate, selectedDate, true);
  if (!values.length && allowWholeBundleFallback) {
    // Fallback sem filtro de dia: só quando o fetch foi de 1 dia,
    // para não atribuir FC de outros dias ao dia sem amostras.
    values = collectHeartRateBpms(bundle.heartRate, selectedDate, false);
  }
  if (values.length) {
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  }
  return averageEcgBpm(
    bundle.electrocardiogram,
    selectedDate,
    allowWholeBundleFallback
  );
}

function parseWeightGrams(value: number | string | undefined): number {
  if (value == null || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function resolveWeightKgForDay(
  points: IGoogleHealthFitnessBundle["weight"],
  selectedDate: Dayjs,
  allowWholeBundleFallback: boolean
): number {
  if (!points.length) return 0;

  const pickLatest = (list: IGoogleHealthFitnessBundle["weight"]) => {
    let latestMs = -1;
    let latestGrams = 0;

    for (const point of list) {
      const grams = parseWeightGrams(point.weight?.weightGrams);
      if (grams <= 0) continue;

      const physical = point.weight?.sampleTime?.physicalTime;
      const ms = physical ? dayjs(physical).valueOf() : 0;
      if (ms >= latestMs) {
        latestMs = ms;
        latestGrams = grams;
      }
    }

    return latestGrams > 0 ? Math.round((latestGrams / 1000) * 10) / 10 : 0;
  };

  const onDay = points.filter((point) =>
    sampleOnSelectedDay(point.weight?.sampleTime, selectedDate)
  );
  const fromDay = pickLatest(onDay);
  if (fromDay > 0) return fromDay;

  // Fallback (última pesagem do bundle): só quando o fetch foi de 1 dia,
  // para não repetir o mesmo peso em todos os dias do período.
  if (!allowWholeBundleFallback) return 0;

  return pickLatest(points);
}

export function formatarDadosGoogleHealth(
  bundle: IGoogleHealthFitnessBundle | null | undefined,
  selectedDate: Dayjs
): IFormattedFitData {
  if (!bundle) {
    return {
      passos: 0,
      calorias: 0,
      distanciaMetros: 0,
      bpmMedio: 0,
      minutosAtivos: 0,
      glicose: 0,
      sonoMinutos: 0,
      pressaoSistolica: 0,
      pressaoDiastolica: 0,
      pressao: "0/0 mmHg",
      pesoKg: 0,
    };
  }

  // Fallbacks "soma tudo" só quando o fetch cobriu um único dia (flag do bundle).
  // Bundle antigo sem a flag é tratado como período (mais seguro: nunca vaza dado).
  const allowFallback = bundle.coversSingleDay === true;

  const bpmMedio = resolveAverageHeartRateBpm(bundle, selectedDate, allowFallback);

  return {
    passos: sumStepsForDay(bundle.steps, selectedDate, allowFallback),
    calorias: 0,
    distanciaMetros: sumDistanceMetersForDay(
      bundle.distance,
      selectedDate,
      allowFallback
    ),
    bpmMedio,
    minutosAtivos: 0,
    glicose: 0,
    sonoMinutos: extractSleepMinutesFromGoogleHealth(
      bundle.sleep,
      selectedDate,
      allowFallback
    ),
    pressaoSistolica: 0,
    pressaoDiastolica: 0,
    pressao: "0/0 mmHg",
    pesoKg: resolveWeightKgForDay(bundle.weight, selectedDate, allowFallback),
  };
}

export function hasGoogleHealthMetrics(
  bundle: IGoogleHealthFitnessBundle | null | undefined,
  selectedDate: Dayjs
): boolean {
  const fit = formatarDadosGoogleHealth(bundle, selectedDate);
  return hasFormattedFitData(fit);
}

function hasFormattedFitData(fit: IFormattedFitData): boolean {
  return (
    fit.passos > 0 ||
    fit.distanciaMetros > 0 ||
    fit.sonoMinutos > 0 ||
    fit.bpmMedio > 0 ||
    fit.pesoKg > 0 ||
    fit.calorias > 0 ||
    fit.minutosAtivos > 0 ||
    fit.glicose > 0 ||
    fit.pressaoSistolica > 0 ||
    fit.pressaoDiastolica > 0
  );
}

/** Lista chaves YYYY-MM-DD entre início e fim (inclusive). */
export function buildFitnessDateKeysInRange(
  periodStart: Dayjs,
  periodEnd: Dayjs
): string[] {
  const keys: string[] = [];
  let cursor = periodStart.startOf("day");
  const end = periodEnd.startOf("day");
  while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
    keys.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }
  return keys;
}

export interface IFitnessPeriodAggregates {
  /** Valores somados no período (métricas acumuláveis). */
  total: IFormattedFitData;
  /** Médias diárias (somente dias com dado > 0). */
  average: IFormattedFitData;
  /** Dias com ao menos uma métrica preenchida. */
  daysWithAnyData: number;
  /** Noites com sono válido (> 0 min) — denominador da média de sono. */
  daysWithSleepData: number;
}

function averagePositive(values: number[]): number {
  const valid = values.filter((v) => v > 0);
  if (!valid.length) return 0;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

/** Total e média de sono apenas sobre noites com valor válido (> 0). */
function aggregateSleepMinutesForPeriod(values: number[]): {
  total: number;
  average: number;
  daysWithData: number;
} {
  const valid = values.filter((minutes) => minutes > 0);
  const daysWithData = valid.length;
  const total = valid.reduce((sum, minutes) => sum + minutes, 0);
  const average = daysWithData > 0 ? total / daysWithData : 0;
  return { total, average, daysWithData };
}

/**
 * Agrega métricas Google Health em um período para exibição (total + média).
 * Cada dia usa a mesma lógica de `formatarDadosGoogleHealth`.
 */
export function aggregateGoogleHealthForPeriod(
  bundle: IGoogleHealthFitnessBundle | null | undefined,
  periodStart: Dayjs,
  periodEnd: Dayjs
): IFitnessPeriodAggregates {
  const empty: IFormattedFitData = {
    passos: 0,
    calorias: 0,
    distanciaMetros: 0,
    bpmMedio: 0,
    minutosAtivos: 0,
    glicose: 0,
    sonoMinutos: 0,
    pressaoSistolica: 0,
    pressaoDiastolica: 0,
    pressao: "0/0 mmHg",
    pesoKg: 0,
  };

  if (!bundle) {
    return { total: empty, average: empty, daysWithAnyData: 0, daysWithSleepData: 0 };
  }

  const dailyFits = buildFitnessDateKeysInRange(periodStart, periodEnd).map(
    (key) => formatarDadosGoogleHealth(bundle, dayjs(key))
  );

  const daysWithAnyData = dailyFits.filter(hasFormattedFitData).length;
  const sleepPeriod = aggregateSleepMinutesForPeriod(
    dailyFits.map((fit) => fit.sonoMinutos)
  );

  const totalPassos = dailyFits.reduce((s, d) => s + d.passos, 0);
  const totalCalorias = dailyFits.reduce((s, d) => s + d.calorias, 0);
  const totalDistancia = dailyFits.reduce((s, d) => s + d.distanciaMetros, 0);
  const totalMinutosAtivos = dailyFits.reduce((s, d) => s + d.minutosAtivos, 0);

  const avgBpm = averagePositive(dailyFits.map((d) => d.bpmMedio));
  const avgPeso = averagePositive(dailyFits.map((d) => d.pesoKg));
  const avgGlicose = averagePositive(dailyFits.map((d) => d.glicose));
  const avgSistolica = averagePositive(dailyFits.map((d) => d.pressaoSistolica));
  const avgDiastolica = averagePositive(dailyFits.map((d) => d.pressaoDiastolica));

  const total: IFormattedFitData = {
    passos: totalPassos,
    calorias: totalCalorias,
    distanciaMetros: totalDistancia,
    bpmMedio: Math.round(avgBpm),
    minutosAtivos: totalMinutosAtivos,
    glicose: Math.round(avgGlicose),
    sonoMinutos: Math.round(sleepPeriod.total * 10) / 10,
    pressaoSistolica: Math.round(avgSistolica),
    pressaoDiastolica: Math.round(avgDiastolica),
    pressao: `${Math.round(avgSistolica)}/${Math.round(avgDiastolica)} mmHg`,
    pesoKg: Math.round(avgPeso * 10) / 10,
  };

  const average: IFormattedFitData = {
    passos: Math.round(averagePositive(dailyFits.map((d) => d.passos))),
    calorias: Math.round(averagePositive(dailyFits.map((d) => d.calorias))),
    distanciaMetros: Math.round(averagePositive(dailyFits.map((d) => d.distanciaMetros))),
    bpmMedio: Math.round(avgBpm),
    minutosAtivos: Math.round(averagePositive(dailyFits.map((d) => d.minutosAtivos))),
    glicose: Math.round(avgGlicose),
    sonoMinutos: Math.round(sleepPeriod.average * 10) / 10,
    pressaoSistolica: Math.round(avgSistolica),
    pressaoDiastolica: Math.round(avgDiastolica),
    pressao: `${Math.round(avgSistolica)}/${Math.round(avgDiastolica)} mmHg`,
    pesoKg: Math.round(avgPeso * 10) / 10,
  };

  return {
    total,
    average,
    daysWithAnyData,
    daysWithSleepData: sleepPeriod.daysWithData,
  };
}

export function hasGoogleHealthMetricsInPeriod(
  bundle: IGoogleHealthFitnessBundle | null | undefined,
  periodStart: Dayjs,
  periodEnd: Dayjs
): boolean {
  return aggregateGoogleHealthForPeriod(bundle, periodStart, periodEnd).daysWithAnyData > 0;
}
